import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import WPDropdown from "../../packages/WPDropdown";
import { WPRangedInput } from "../../packages/WPInput/WPRangedInput";
import ToggleSwitch from "../../common/styles/components/switches/CustomSwitch";
import {
  Default_CIP_INFORMATION,
  CIP_SECTION_META,
  DEFAULT_CIP_SECTION_KEYS,
  getCIPPriceRanges,
} from "./defaultCIPInformationFields.config";
import {
  CURRENCY_UNITS,
  CATEGORY_TO_CHEM_ID_KEY,
  CATEGORY_TO_ENABLED_KEY,
  DEFAULT_CIP_KEYS,
} from "../../constants/global.constants";
import { getChemicalOptionsByCategory } from "../../components/ChemicalSelectionFields/utils";
import { ensureChemicalLibraryHydrated } from "../../components/chemicalLibrary/ChemicalLibraryDialog/ChemicalLibraryDialog.api";

const [ACID_KEY, ALKALI_KEY, CIP_ADDITIVE_KEY] = DEFAULT_CIP_KEYS;

const parseEnabledValue = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();
    if (normalizedValue === "1" || normalizedValue === "true") {
      return true;
    }

    if (normalizedValue === "0" || normalizedValue === "false") {
      return false;
    }
  }

  return false;
};

const formatPriceValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

const CIPSection = ({
  defaultCIPKeys,
  errors,
  handleInputChange,
  onFieldChange,
  onMandatoryValidationChange,
  onSectionValidationToggle,
  disabled,
  existingCIPState,
  idPrefix = "default_cip",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    temperature,
    mass,
    currency,
    conversion_rate,
    plantChemicalLibrary,
    userChemicalLibrary,
  } = useSelector((state) => state);

  const defaultCIPState = useSelector(
    (state) => ({
      acid_chem_id: state.acid_chem_id,
      alkali_chem_id: state.alkali_chem_id,
      additional_chemical_chem_id: state.additional_chemical_chem_id,
      acid_price: state.acid_price,
      alkali_price: state.alkali_price,
      additional_chemical_price: state.additional_chemical_price,
      acid_enabled: state.acid_enabled,
      alkali_enabled: state.alkali_enabled,
      additional_chemical_enabled: state.additional_chemical_enabled,
      acid_used_per_cip: state.acid_used_per_cip,
      alkali_used_per_cip: state.alkali_used_per_cip,
      additional_chemical_per_cip: state.additional_chemical_per_cip,
      acid_cip_ph: state.acid_cip_ph,
      alkali_cip_ph: state.alkali_cip_ph,
      cip_solution_ph: state.cip_solution_ph,
      acid_cip_temp: state.acid_cip_temp,
      alkali_cip_temp: state.alkali_cip_temp,
      cip_solution_temp: state.cip_solution_temp,
      acid_cip_duration: state.acid_cip_duration,
      alkali_cip_duration: state.alkali_cip_duration,
      cip_solution_duration: state.cip_solution_duration,
    }),
    shallowEqual,
  );

  const chemicalLibrary = useMemo(() => {
    if (plantChemicalLibrary?.length) {
      return plantChemicalLibrary;
    }

    return userChemicalLibrary || [];
  }, [plantChemicalLibrary, userChemicalLibrary]);

  const chemicalOptionsByCategory = useMemo(() => {
    const optionsByChemicalField =
      getChemicalOptionsByCategory(chemicalLibrary);

    return {
      acid: optionsByChemicalField?.acid || [],
      alkali: optionsByChemicalField?.alkali || [],
      cip_additive: optionsByChemicalField?.additional_chemical || [],
    };
  }, [chemicalLibrary]);

  useEffect(() => {
    if (chemicalLibrary?.length) {
      return;
    }

    const fetchData = async () => {
      await ensureChemicalLibraryHydrated(dispatch, t);
    };

    fetchData();
  }, []);

  const getDefaultChemicalOption = (category) => {
    const options = chemicalOptionsByCategory?.[category] || [];
    if (!options.length) return null;

    const chemIdKey = CATEGORY_TO_CHEM_ID_KEY[category];
    const defaultChemicalId =
      existingCIPState?.[chemIdKey] ?? defaultCIPState?.[chemIdKey];

    return (
      options.find(
        (option) => Number(option.value) === Number(defaultChemicalId),
      ) || null
    );
  };

  const [selectedChemicals, setSelectedChemicals] = useState(() => ({
    [ACID_KEY]: getDefaultChemicalOption(ACID_KEY),
    [ALKALI_KEY]: getDefaultChemicalOption(ALKALI_KEY),
    [CIP_ADDITIVE_KEY]: getDefaultChemicalOption(CIP_ADDITIVE_KEY),
  }));

  useEffect(() => {
    setSelectedChemicals({
      [ACID_KEY]: getDefaultChemicalOption(ACID_KEY),
      [ALKALI_KEY]: getDefaultChemicalOption(ALKALI_KEY),
      [CIP_ADDITIVE_KEY]: getDefaultChemicalOption(CIP_ADDITIVE_KEY),
    });
  }, [chemicalOptionsByCategory, existingCIPState, defaultCIPState]);

  const getSectionEnabled = (category) => {
    const enabledKey = CATEGORY_TO_ENABLED_KEY[category];
    return parseEnabledValue(
      existingCIPState?.[enabledKey] ?? defaultCIPState?.[enabledKey],
    );
  };

  const getPriceValue = (category) => {
    const priceKey = CIP_SECTION_META[category]?.priceKey;
    if (!priceKey) {
      return "";
    }

    const explicitPrice =
      existingCIPState?.[priceKey] ?? defaultCIPState?.[priceKey];

    if (explicitPrice !== undefined && explicitPrice !== null) {
      return formatPriceValue(explicitPrice);
    }

    return "";
  };
  const defaultFields = useMemo(
    () => Default_CIP_INFORMATION(temperature, mass),
    [temperature, mass],
  );
  const activePriceRangeByCategory = useMemo(
    () => getCIPPriceRanges(currency, mass, conversion_rate, defaultCIPKeys),
    [currency, mass, conversion_rate, defaultCIPKeys],
  );

  const handleChemicalChange = (category, option) => {
    setSelectedChemicals((prev) => ({
      ...prev,
      [category]: option,
    }));

    const sectionMeta = CIP_SECTION_META[category] || {};
    if (onFieldChange && sectionMeta.chemicalKey) {
      onFieldChange(sectionMeta.chemicalKey, String(option?.value || ""));
    }

    if (option?.price !== undefined && option?.price !== null) {
      const updatedPrice = formatPriceValue(option.price);
      const minValue = activePriceRangeByCategory?.[category]?.min;
      const maxValue = activePriceRangeByCategory?.[category]?.max;

      if (handleInputChange && sectionMeta.priceKey) {
        handleInputChange(
          sectionMeta.priceKey,
          updatedPrice,
          minValue,
          maxValue,
        );
      } else if (onFieldChange && sectionMeta.priceKey) {
        onFieldChange(sectionMeta.priceKey, updatedPrice);
      }
    }
  };

  const handlePriceChange = (category, value) => {
    const priceKey = CIP_SECTION_META[category]?.priceKey;
    const minValue = activePriceRangeByCategory?.[category]?.min;
    const maxValue = activePriceRangeByCategory?.[category]?.max;

    if (handleInputChange && priceKey) {
      handleInputChange(priceKey, value, minValue, maxValue);
    } else if (onFieldChange && priceKey) {
      onFieldChange(priceKey, value);
    }
  };

  const handleSectionToggle = (category, isEnabled) => {
    const enabledKey = CATEGORY_TO_ENABLED_KEY[category];
    if (onFieldChange && enabledKey) {
      onFieldChange(enabledKey, Boolean(isEnabled));
    }

    if (onSectionValidationToggle) {
      onSectionValidationToggle(
        category,
        Boolean(isEnabled),
        Object.values(defaultFields?.[category] || []),
      );
    }
  };

  const getValidationFieldsForCategory = (category) => {
    const sectionFields = Object.values(defaultFields?.[category] || []);
    const priceKey = CIP_SECTION_META[category]?.priceKey;

    if (!priceKey) {
      return sectionFields;
    }

    return [
      {
        key: priceKey,
        ranges: {
          hard: {
            min: activePriceRangeByCategory?.[category]?.min,
            max: activePriceRangeByCategory?.[category]?.max,
          },
        },
      },
      ...sectionFields,
    ];
  };

  useEffect(() => {
    if (!onSectionValidationToggle || !defaultCIPKeys?.length) {
      return;
    }

    defaultCIPKeys.forEach((category) => {
      onSectionValidationToggle(
        category,
        getSectionEnabled(category),
        getValidationFieldsForCategory(category),
      );
    });
  }, [
    activePriceRangeByCategory,
    defaultCIPKeys,
    existingCIPState,
    defaultCIPState,
    onSectionValidationToggle,
  ]);

  useEffect(() => {
    const hasMissingRequiredFields = (defaultCIPKeys || []).some((key) => {
      const isEnabled = getSectionEnabled(key);
      if (!isEnabled) return false;

      const selectedChemical = selectedChemicals?.[key];
      const priceValue = getPriceValue(key);

      return !selectedChemical?.label || priceValue === "";
    });

    if (onMandatoryValidationChange) {
      onMandatoryValidationChange(hasMissingRequiredFields);
    }
  }, [
    defaultCIPKeys,
    existingCIPState,
    defaultCIPState,
    onMandatoryValidationChange,
    selectedChemicals,
  ]);

  return (
    <div className="sub-grid-container">
      {defaultCIPKeys?.map((key) => {
        const selectedChemical = selectedChemicals?.[key] || null;
        const currencyUnit = CURRENCY_UNITS[currency] || "$";
        const sectionEnabled = getSectionEnabled(key);
        const sectionDisabled = disabled || !sectionEnabled;

        return (
          <div key={key} className="default-cip-section">
            <div id={`train-chemical-${key}-selection`}>
              <div className="default-cip-section-header">
                <div className="section-title">
                  {t(CIP_SECTION_META[key]?.titleKey || "") || `${key} CIP`}
                </div>
                <ToggleSwitch
                  id={`${idPrefix}_toggle_${key}`}
                  checked={sectionEnabled}
                  onChange={(checked) => handleSectionToggle(key, checked)}
                  small
                  optionLabels={["ON", "OFF"]}
                  disabled={disabled}
                  className="default-cip-toggle"
                />
              </div>
              <div className="chemical-price-row">
                <div className="faux-input">
                  <WPDropdown
                    id={`chemical_dropdown_${key}`}
                    label={t(CIP_SECTION_META[key]?.chemicalLabelKey)}
                    value={selectedChemical}
                    options={chemicalOptionsByCategory?.[key] || []}
                    onChange={(option) => handleChemicalChange(key, option)}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => String(option.value)}
                    placeholder={t(CIP_SECTION_META[key]?.chemicalLabelKey)}
                    required
                    disabled={sectionDisabled}
                    customClassName="default-cip-chemical-dropdown"
                  />
                </div>
                <div className="faux-input">
                  <WPRangedInput
                    id={`chemical_price_${key}`}
                    label={t("defaultInformationStrings.price")}
                    value={getPriceValue(key)}
                    onChange={(e) => handlePriceChange(key, e.target.value)}
                    ranges={{
                      hard: {
                        min: activePriceRangeByCategory?.[key]?.min,
                        max: activePriceRangeByCategory?.[key]?.max,
                      },
                    }}
                    fractions={3}
                    maxDecimals={3}
                    required
                    requiredMsg={t("ValueStrings.requiredField")}
                    suffix={`${currencyUnit}/${mass}`}
                    disabled={sectionDisabled}
                    skipValidation={sectionDisabled}
                    externalError={
                      sectionDisabled
                        ? null
                        : errors?.[CIP_SECTION_META[key]?.priceKey]
                    }
                    showRangeAlways
                    hideRangeAlways={sectionDisabled}
                  />
                </div>
              </div>
            </div>
            <div className="sub-grid-item">
              {Object.values(defaultFields[key] || {}).map((field, index) => (
                <WPRangedInput
                  key={index}
                  value={
                    existingCIPState &&
                    existingCIPState[field.key] !== undefined
                      ? existingCIPState[field.key]
                      : defaultCIPState[field.key]
                  }
                  name={field.key}
                  label={t(`defaultInformationStrings.${field.labelKey || field.key}`)}
                  suffix={field.unit}
                  onChange={
                    sectionDisabled
                      ? undefined
                      : (e) =>
                          handleInputChange(
                            field.key,
                            e.target.value,
                            field.ranges?.hard?.min,
                            field.ranges?.hard?.max,
                          )
                  }
                  ranges={field.ranges}
                  disabled={sectionDisabled}
                  skipValidation={sectionDisabled}
                  required
                  requiredMsg={t("ValueStrings.requiredField")}
                  externalError={sectionDisabled ? null : errors?.[field.key]}
                  showRangeAlways
                  hideRangeAlways={sectionDisabled}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CIPSection;
